<template>
  <div class="download-success-view">
    <loading-component
      v-if="isLoading"
      from="Download Success"
    />
    <div>
      <PageLayout
        v-if="!isDownloading"
        :title="$t('loginAccountSuccess.title')"
      >
        <a
          class="simple-link"
          @click.prevent="startDownload('click')"
        >
          {{ $t('loginAccountSuccess.download.redirect') }}
        </a>
      </PageLayout>
      <PageLayout
        v-else
        :title="title"
      >
        <DownloadFlagshipComponent
          v-if="mac.isMac"
          :arm64-url="mac.arm64DownloadUrl"
          :intel-url="mac.intelDownloadUrl"
          @downloadLinkClicked="sendDownloadMetrics"
          @crossOsDownload="crossOsDownload"
        />
        <DownloadWindowsComponent
          v-if="windows.isWindows"
          :user-creation-date="getUserCreationDate()"
          @onDownloadStart="onDownloadStart"
          @crossOsDownload="crossOsDownload"
        />
      </PageLayout>
    </div>
  </div>
</template>

<script>
import { mParticleMetric } from '@/metrics';
import { CookieService } from '@/services/cookie';
import { LOGIN_ACTIONS_CONSTANTS } from '@/domain/login';
import { ROUTER_PATHS } from '@/router/paths';
import { metricsService } from  '@/metrics/metrics.js';
import userAgentParser from '@voicemod1/user-agent-decoder';
import { DownloadClient } from '@/services/download';
import { AccountClient } from '@/services/models/accounts';
import PageLayout from '@/components/PageLayout/PageLayout.vue';
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent';
import * as Sentry from '@sentry/vue';
import { OS_NAMES } from '@/domain/user/constants';

import DownloadFlagshipComponent from '@/components/DownloadFlagshipComponent/DownloadFlagshipComponent';
import DownloadWindowsComponent from '@/components/DownloadWindowsComponent/DownloadWindowsComponent';

const USER_ATTRIBUTE_AB_TEST_SPLIT = 'Web_AB_Test_Split';
const ONE_YEAR_IN_SECONDS = 12 * 30 * 24 * 60 * 60;
const START_DOWNLOAD_TIMEOUT = 2000;

export default {
    components: {
        PageLayout,
        LoadingComponent,
        DownloadFlagshipComponent,
        DownloadWindowsComponent,
    },
    data() {
        return {
            isDownloading: false,
            userId: null,
            isNewUser: false,
            downloadInfo: null,
            clickSource: null,
            isLoading: true,
            operationSystem: null,
            forceOs: null,
            mac: {
                isMac: true,
                arm64DownloadUrl: null,
                intelDownloadUrl: null,
            },
            windows: {
                isWindows: false,
                forceV2: false,
                isOldUser: false,
            },
        };
    },
    beforeMount() {
        const userAgent = userAgentParser.parse(navigator.userAgent);
        this.operationSystem = userAgent.getOSName();

        this.mac.isMac = this.operationSystem === OS_NAMES.mac;
        this.windows.isWindows = this.operationSystem === OS_NAMES.windows;

        this.setTitle();
    },
    async mounted() {
        metricsService.initMetrics();

        this.userId = this.$route.query.userId;
        this.isNewUser = this.$route.query.isNew === 'true';

        this.clickSource = this.$route.query.clickSource ?? null;

        const cookieUserId = CookieService.getCookie('userId');
        const extraParams = this.getExtraParamsFromUrl();

        if (this.userId !== cookieUserId) {
            this.$router.push({
                path: ROUTER_PATHS.HOME,
                query: {
                    action: LOGIN_ACTIONS_CONSTANTS.DOWNLOAD,
                    ...extraParams,
                },
            });
            return;
        }

        this.$store.dispatch('user_info_store/axn_setShowProfile', true);
        CookieService.setCookie('userId', this.userId);

        if (!mParticleMetric.isMParticleUserIdSet()) {
            Sentry.addBreadcrumb({ message: 'DonwloadSuccesView: mParticle Id not set, initiating mParticle login', level: 'info' });
            mParticleMetric.login({ customerid: this.userId });
        }

        mParticleMetric.track(mParticleMetric.EVENTS.DOWNLOAD_VIEW, {
            download_click_source: this.clickSource,
        });

        this.userInfo = await this.getUserInfo();

        await this.setDownloadInfo(this.operationSystem);
    },
    methods: {
        setTitle() {
            if (this.windows.isWindows) {
                this.title = this.$t('loginAccountSuccess.download.title');
            } else {
                this.title = this.$t('appleDownloadView.title');
            }
        },
        async setDownloadInfo(OS) {
            try {
                if (OS === OS_NAMES.windows) {
                    this.downloadInfo = await this.getWindowsDownloadData();
                } else if (OS === OS_NAMES.mac) {
                    this.downloadInfo = await this.getMacDownloadData();
                }

                this.isLoading = false;

                const downloadId = CookieService.getCookie('download_id');
                if (!downloadId && this.downloadInfo.downloadId) {
                    const expiresInOneYear = parseInt(Date.now() / 1000) + ONE_YEAR_IN_SECONDS;
                    CookieService.setCookie('download_id', this.downloadId, expiresInOneYear );
                }

                if (this.downloadInfo.Web_AB_Test_Split) {
                    mParticleMetric.setUserAttribute(USER_ATTRIBUTE_AB_TEST_SPLIT, this.downloadInfo.Web_AB_Test_Split);
                }

                setTimeout(() => this.startDownload('auto'), START_DOWNLOAD_TIMEOUT);
            } catch (error) {
                Sentry.captureMessage('DownloadView: Error getting download Data');
                Sentry.captureException(error);
                this.$router.push(ROUTER_PATHS.ACCOUNT_ACTIVATED_ERROR);
                return;
            }
        },
        async getWindowsDownloadData() {
            try {
                const customHeader = {
                    'Content-Type': 'application/json',
                    'X-Creation-Date': this.getUserCreationDate(),
                    'X-User-Type': this.getUserType(),
                };

                const { data } = await DownloadClient.getAppDownloadInfo({
                    headers: customHeader,
                });

                if (!data.url) {
                    throw new Error('Download info is empty');
                }

                return data;
            } catch (error) {
                Sentry.captureMessage('Request to download windows endpoint failed');
                Sentry.captureException(error);
                mParticleMetric.track(mParticleMetric.EVENTS.ACCOUNTS_WEB_UNEXPECTED_ERROR_OCCURRED, {
                    login_error_reason: 'request_to_download_endpoint_failed',
                });

                throw error;
            }
        },
        async getMacDownloadData() {
            try {
                const { data } = await DownloadClient.getFlagshipAppDownloadInfo();

                if (!data.arm64 && !data.x86_64) {
                    throw new Error('Download info is empty');
                }

                this.mac.arm64DownloadUrl = data.arm64;
                this.mac.intelDownloadUrl = data.x86_64;

                return data;
            } catch (error) {
                Sentry.captureMessage('Can\'t download Flagship');
                Sentry.captureException(error);
                mParticleMetric.track(mParticleMetric.EVENTS.ACCOUNTS_WEB_UNEXPECTED_ERROR_OCCURRED, {
                    login_error_reason: 'flagship_request_to_download_endpoint_failed',
                });

                throw error;
            }
        },
        startDownload(type) {
            this.isDownloading = true;

            if (this.operationSystem === OS_NAMES.windows && !this.forceOs || this.forceOs === OS_NAMES.windows) {
                this.onDownloadStart(type);
            }
        },
        onDownloadStart(type, v2Forced = false) {
            const { url } = this.getDownloadUrlAndVersion(v2Forced);

            if (!url) {
                Sentry.captureMessage('DownloadSuccessView: On link click Empty URL');
            }

            this.sendDownloadMetrics(type);
            window.location.href = url;
        },
        sendDownloadMetrics(source, arch = null) {
            const { product, url, version, isV2Forced } = this.getDownloadUrlAndVersion(null, arch);

            if (mParticleMetric) {
                mParticleMetric.track(mParticleMetric.EVENTS.DOWNLOAD_TRIGGERED, {
                    source: source,
                    app_version: version,
                    cpu_architecture: arch,
                    force_v2: isV2Forced,
                    download_click_source: this.clickSource,
                });
            }

            if (window.dataLayer) {
                window.dataLayer.push({event: 'downloadTriggered'});
                window.dataLayer.push({
                    'event': 'Download Triggered',
                    'product': product,
                    'cpu_architecture': arch,
                    'app_version': version,
                    'file_name': url,
                });
            }
        },
        getDownloadUrlAndVersion(v2Forced = null, arch = null) {
            const trackingData = {
                product: '',
                url: '',
                version: null,
                isV2Forced: null,
            };

            if (this.operationSystem === OS_NAMES.windows && !this.forceOs || this.forceOs === OS_NAMES.windows) {
                trackingData.product = 'V2';
                trackingData.url = this.downloadInfo.url;
                trackingData.isV2Forced = false;
                trackingData.version = this.downloadInfo.app_version;

                if (this.windows.forceV2 && this.downloadInfo.urlV2 && !this.forceOs || v2Forced && this.downloadInfo.urlV2) {
                    trackingData.url = this.downloadInfo.urlV2;
                    trackingData.version = this.downloadInfo.app_version_v2;
                    trackingData.isV2Forced = true;
                }
            } else if (this.operationSystem === OS_NAMES.mac || this.forceOs === OS_NAMES.mac) {
                trackingData.product = 'Flagship';
                trackingData.version = this.downloadInfo.appVersion;

                trackingData.url = arch === 'arm64' ? this.mac.arm64DownloadUrl : this.mac.intelDownloadUrl;
            }

            return trackingData;
        },
        async getUserInfo() {
            const userInfo = sessionStorage.getItem('userInfo');

            if (userInfo) {
                try {
                    return JSON.parse(userInfo);
                } catch (error) {
                    Sentry.captureMessage('DownloadSuccessView: User info is invalid');
                    Sentry.captureException(error);
                }
            }

            try {
                const { data } = await AccountClient.getUserInfo(this.userId);
                this.$store.dispatch('user_info_store/axn_setUserInfo', {userId: this.userId, ...data});
                sessionStorage.setItem('userInfo', JSON.stringify({userId: this.userId, ...data}));
                return {userId: this.userId, ...data};
            } catch (error) {
                Sentry.captureMessage('DownloadSuccessView: User info is empty');
                Sentry.captureException(error);
            }
        },
        getUserCreationDate() {
            if (!this.userInfo || !this.userInfo.createdAt) {
                Sentry.captureMessage('DownloadSuccessView: User creation date is missing or invalid');
                return '';
            }

            return this.userInfo.createdAt;
        },
        getUserType() {
            if (!this.userInfo || !this.userInfo.subscription || !this.userInfo.subscription.subscriptionType) {
                Sentry.captureMessage('DownloadSuccessView: User Type is missing or invalid');
                return '';
            }
            return this.userInfo.subscription.subscriptionType;
        },
        getExtraParamsFromUrl() {
            const params = {};

            if (this.windows.isWindows) {
                if (this.$route.query.forcev2 === 'true') {
                    params['forceV2'] = true;
                    this.windows.forceV2 = true;
                }
            }

            return params;
        },
        async crossOsDownload(OS) {
            this.isLoading = true;
            this.mac.isMac = OS === OS_NAMES.mac;
            this.windows.isWindows = OS === OS_NAMES.windows;
            this.forceOs = OS;

            this.setTitle();

            if (mParticleMetric) {
                mParticleMetric.track(mParticleMetric.EVENTS.CROSS_OS_LINK, {
                    source: 'click',
                    requested_os: OS,
                    current_os: this.operationSystem,
                });
            }

            await this.setDownloadInfo(OS);
        },
    },
};
</script>

<style src="./DownloadSuccessView.scss" lang="scss"></style>
